import { removeRoomlessSelectedPackages } from 'lib/offer/multipleRoomBookingUtils'
import { createReducer } from 'lib/redux/reducerUtils'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import { ValueOf } from 'type-fest'
import { isBundleOffer } from 'lib/offer/offerTypes'
import config from 'constants/config'
import { isBundleAndSaveV2Enabled } from 'lib/offer/offerUtils'
import { EmptyArray } from 'lib/array/arrayUtils'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { Reducer } from 'react'

export enum OfferPageActions {
  checkAvailability = 'checkAvailability',
  closeFilter = 'closeFilter',
  clearFilter = 'clearFilter',
  closeSubFilter = 'closeSubFilter',
  filterContinue = 'filterContinue',
  filterBack = 'filterBack',
  resetDates = 'resetDates',
  setDepartureMonth = 'selectDepartureMonthIndex',
  setDepartureYear = 'selectDepartureYear',
  setTourV2Departure = 'selectTourV2OfferDeparture',
  setTourV2Variation = 'selectTourVOfferVariation',
  setAirport = 'setAirport',
  setPaginatorCurrentOfferId = 'setPaginatorCurrentOfferId',
  setBedBankOffer = 'setBedBankOffer',
  setCheckInDate = 'setCheckInDate',
  setCheckOutDate = 'setCheckOutDate',
  setDates = 'setDates',
  showRecommendationsView = 'showRecommendationsView',
  hideRecommendationsView = 'hideRecommendationsView',
  setDuration = 'setDuration',
  setFilteredBundlePackageId = 'setFilteredBundlePackageId',
  reverseBundles = 'reverseBundles',
  setFlights = 'setFlights',
  setOffer = 'setOffer',
  setRoomType = 'setRoom',
  setRooms = 'setRooms',
  setTourV2Offer = 'setTourV2Offer',
  toggleFilter = 'toggleFilter',
  openSwapModal = 'openSwapModal',
  toggleSubFilter = 'toggleSubFilter',
  setRoomOption = 'setRoomOption',
  selectRoomsPackages = 'selectRoomsPackages',
  selectRoomPackage = 'selectRoomPackage',
  toggleFlightsPrice = 'toggleFlightsPrice',
  setBedbankRoomId = 'setBedbankRoomId',
  setDisplayMemberPrice = 'setDisplayMemberPrice',
  toggleConnectedRooms = 'toggleConnectedRooms',
  setKeepBrowsing = 'setKeepBrowsing',
  setLastInteractedCalendarDay = 'setLastInteractedCalendarDay',
  toggleLoyaltyTargetRoomUpgrade = 'toggleLoyaltyTargetRoomUpgrade',
  toggleHeaderFilter = 'toggleHeaderFilter',
  closeHeaderFilter = 'closeHeaderFilter',
  toggleMobileMultiRoomMode = 'toggleMobileMultiRoomMode',
  toggleMultiRoomSelectRooms = 'toggleMultiRoomSelectRooms',
}

export type OfferPageFilterType = 'all' | 'duration' | 'capacity' | 'airport' | 'checkin' | 'package' | 'room' | 'roomSelection' | 'packageSelection' | 'flights-toggle' | 'roomDetails' | 'multiRoomSelect' | 'multiRoomConfirm'
export type MobileMultiRoomMode = 'individual' | 'same-room'

export type SelectedMultipleRoomPackage = {
  roomId: string
  packageId: string
  roomRateId: string
  isFlightsSelected: boolean
  roomTypeId: string
}
export type MultipleRoomPackage = SelectedMultipleRoomPackage | {
  roomId: string
  packageId: undefined
  roomRateId: undefined
  isFlightsSelected: boolean
  roomTypeId: undefined
}

export interface OfferPageRoomOption {
  roomRateId: string;
  roomTypeId: string;
}

export interface BundledOfferState {
  offer?: App.HotelOffer
  checkInDate?: string
  checkOutDate?: string
  duration?: number
  room?: App.PackageRoomType
  activeFilter?: OfferPageFilterType
}

export interface PaginatorState {
  currentOfferId?: string
  nextOfferId?: string
  backOfferId?: string
}

export interface TourV2MonthFilter {
  month: number,
  year: number
}

export interface OfferPageState {
  activeFilter?: OfferPageFilterType
  activeSubFilter?: OfferPageFilterType
  activeHeaderFilter?: OfferPageFilterType
  filterHistory: Array<OfferPageFilterType>;
  airport?: App.Airport
  bedbankOffer?: App.BedbankOffer
  checkInDate?: string
  /**
   * This field only exits for bedbank calendars that do not choose calendar days
   * but choose dates instead.
   *
   * Would recommend using the normalised `getCheckoutDate` selector
   */
  checkOutDate?: string
  duration: number
  durationSelected?: boolean;
  filteredBundlePackageId?: string
  bundleOffer?: App.BundleOffer
  bundledOffers: {
    [offerId: string]: BundledOfferState
  }
  bundleSwapModalTicker: number
  paginator?: PaginatorState
  flights: boolean
  isMultiRoomConnected?: boolean
  multipleRoomPackages: Array<MultipleRoomPackage>
  occupancyPicker?: boolean
  offer?: App.HotelOffer | App.BundleOffer | App.VillaOffer;
  package?: App.HotelPackage
  recommendationsCheckInDay?: string
  recommendationsMonthIndex?: string
  room?: App.PackageRoomType
  roomOption?: OfferPageRoomOption
  rooms?: Array<App.RoomOccupants>
  mobileMultiRoomMode?: MobileMultiRoomMode
  isMultiRoomSelectRooms?: boolean
  keepBrowsing?: boolean

  /**
   * TODO: Remove this field, views should never be stored, they are transient values
   * Perhaps store the package unique key instead
   * */
  selectedPackageView?: OfferPackageOptionView
  showRecommendationsView?: boolean
  selectedBedbankRoomId?: string
  displayMemberPrice?: boolean

  // Tours
  tourV2Offer?: App.Tours.TourV2Offer
  tourV2Departure?: App.Tours.TourV2OfferDeparture
  tourV2Variation?: App.Tours.TourV2OfferVariation
  departureMonth?: TourV2MonthFilter;
  departureYear?: number;

  // Last Interacted OfferCalendarDayCell - Only used for analytics currently
  lastInteractedCalendarDay?: App.CalendarDay
  loyaltyTargetRoomUpgradesSelected?: App.LoyaltyTargetRoomUpgradesSelected;
}

export const INITIAL_OFFER_PAGE_STATE: OfferPageState = {
  filterHistory: [],
  multipleRoomPackages: [],
  bundledOffers: {},
  bundleSwapModalTicker: 0,
  duration: 0,
  flights: false,
}

function setFlightsForAllRooms(state: OfferPageState, flights: boolean): Array<MultipleRoomPackage> {
  if (!state.rooms?.length || state.bedbankOffer) return []

  // Get the list of rooms that are not in the multipleRoomPackages yet (if any)
  const alreadyAddedRooms = new Set((state.multipleRoomPackages ?? []).map(mrpkg => mrpkg.roomId))
  const roomsToAdd =
    state.rooms
      .filter(room => !alreadyAddedRooms.has(room.roomId))
      .map(room => ({ roomId: room.roomId, roomRateId: undefined, packageId: undefined, roomTypeId: undefined }))

  return [...(state.multipleRoomPackages ?? []), ...roomsToAdd].map(mrpkg => ({
    ...mrpkg,
    isFlightsSelected: flights,
  }))
}

export type OfferPageDispatchAction = ValueOf<Utils.FullActionMap<{
  [OfferPageActions.checkAvailability]: {
    flights: OfferPageState['flights'],
    room?: OfferPageState['room'],
    filter: OfferPageState['activeFilter'],
    selectedPackageView?: OfferPageState['selectedPackageView']
  }
  [OfferPageActions.closeFilter]: {
    occupancyPicker?: OfferPageState['occupancyPicker']
  },
  [OfferPageActions.clearFilter]: {},
  [OfferPageActions.closeSubFilter]: {},
  [OfferPageActions.closeHeaderFilter]: {},
  [OfferPageActions.filterContinue]: {},
  [OfferPageActions.filterBack]: {},
  [OfferPageActions.resetDates]: {
    offerId?: string,
  },
  [OfferPageActions.setDepartureMonth]: {
    month: OfferPageState['departureMonth'] | undefined,
  },
  [OfferPageActions.setDepartureYear]: {
    year: OfferPageState['departureYear'],
  },
  [OfferPageActions.setTourV2Departure]: {
    departure: OfferPageState['tourV2Departure'],
  },
  [OfferPageActions.setTourV2Variation]: {
    variation: OfferPageState['tourV2Variation'],
  },
  [OfferPageActions.setAirport]: {
    airport: OfferPageState['airport'],
  },
  [OfferPageActions.setBedBankOffer]: {
    bedbankOffer: OfferPageState['bedbankOffer'],
  },
  [OfferPageActions.setPaginatorCurrentOfferId]: {
    currentOfferId: string,
  },
  [OfferPageActions.setCheckInDate]: {
    checkIn: OfferPageState['checkInDate'],
    offerId?: string,
  },
  [OfferPageActions.setCheckOutDate]: {
    checkOut: OfferPageState['checkOutDate'],
    offerId?: string,
  },
  [OfferPageActions.setDates]: {
    checkIn: string | undefined;
    checkOut: string | undefined;
    offerId?: string;
  },
  [OfferPageActions.setDuration]: {
    duration: OfferPageState['duration'],
    offerId?: string,
  },
  [OfferPageActions.setFilteredBundlePackageId]: {
    filteredBundlePackageId: OfferPageState['filteredBundlePackageId'],
  },
  [OfferPageActions.reverseBundles]: {},
  [OfferPageActions.setFlights]: {
    flights: OfferPageState['flights'],
  }
  [OfferPageActions.setOffer]: {
    offer: OfferPageState['offer'],
    bundledOffers?: OfferPageState['bundledOffers'],
  }
  [OfferPageActions.setRoomType]: {
    room: OfferPageState['room'],
    offerId?: string,
  }
  [OfferPageActions.setRooms]: {
    rooms: OfferPageState['rooms'],
  }
  [OfferPageActions.setTourV2Offer]: {
    variation: OfferPageState['tourV2Variation'],
    offer: OfferPageState['tourV2Offer'],
  }
  [OfferPageActions.setRoomOption]: {
    roomOption: OfferPageState['roomOption'],
  }
  [OfferPageActions.toggleFilter]: {
    filter: OfferPageState['activeFilter'],
    keepBrowsing?: OfferPageState['keepBrowsing'],
    offerId?: string
  }
  [OfferPageActions.openSwapModal]: {}
  [OfferPageActions.toggleSubFilter]: {
    filter: OfferPageState['activeSubFilter']
  }
  [OfferPageActions.showRecommendationsView]: {
    monthIndex?: string;
    checkInDay?: string;
  },
  [OfferPageActions.hideRecommendationsView]: {
  },
  [OfferPageActions.selectRoomsPackages]: {
    roomsPackages: Array<{ room: App.RoomOccupants, packageOption: App.HotelPackage }>
  },
  [OfferPageActions.selectRoomPackage]: {
    packageOption: App.HotelPackage,
    room: App.RoomOccupants
  },
  [OfferPageActions.toggleFlightsPrice]: {
    isFlightsSelected: boolean,
  },
  [OfferPageActions.setBedbankRoomId]: {
    bedbankRoomId: OfferPageState['selectedBedbankRoomId'],
  }
  [OfferPageActions.setDisplayMemberPrice]: {
    displayMemberPrice: OfferPageState['displayMemberPrice'],
  }
  [OfferPageActions.toggleConnectedRooms]: {},
  [OfferPageActions.setKeepBrowsing]: {
    keepBrowsing: OfferPageState['keepBrowsing'],
  }
  [OfferPageActions.setLastInteractedCalendarDay]: {
    lastInteractedCalendarDay: OfferPageState['lastInteractedCalendarDay'],
  },
  [OfferPageActions.toggleLoyaltyTargetRoomUpgrade]: {
    selectedUpgradeKey: string,
    hasSelectedTargetRoomUpgrade: boolean,
  }
  [OfferPageActions.toggleHeaderFilter]: {
    filter: OfferPageState['activeHeaderFilter']
  }
  [OfferPageActions.toggleMobileMultiRoomMode]: {
    mobileMultiRoomMode: OfferPageState['mobileMultiRoomMode'],
  }
  [OfferPageActions.toggleMultiRoomSelectRooms]: {
    isMultiRoomSelectRooms: boolean,
  }
}>>

function setBundledOfferData(
  offerId: string,
  field: 'checkInDate' | 'checkOutDate' | 'duration' | 'room' | 'activeFilter',
  state: Partial<OfferPageState>,
  value: OfferPageState['checkInDate'] | OfferPageState['checkOutDate'] | OfferPageState['duration'] | OfferPageState['room'] | OfferPageState['activeFilter'],
) {
  return {
    bundledOffers: {
      ...(state.bundledOffers ?? {}),
      [offerId]: {
        ...(state.bundledOffers?.[offerId] ?? {}),
        [field]: value,
      },
    },
  }
}

export function takeOffersStates(state: OfferPageState) {
  if (isBundleOffer(state.offer)) {
    const attachedOffersIds = Object.keys(state.bundledOffers)

    return attachedOffersIds.map((id) => takeOfferState(state, id))
  }

  return [takeOfferState(state)]
}

export function takeOfferState(state: OfferPageState, offerId?: string): OfferPageState {
  if (isBundleOffer(state.offer) && offerId) {
    return {
      ...state,
      ...state.bundledOffers?.[offerId] ?? {},
    } as any
  }

  return state
}

export function coverByOffersStates<T extends any>(selector: any, ...states: Array<OfferPageState | App.State>): Array<T> {
  const offerPageStateIndex = states.findIndex((state) => 'id' in state.offer!)
  const offerPageStates = takeOffersStates(states[offerPageStateIndex] as OfferPageState)
  return offerPageStates.map((state) => {
    const newState = [...states]
    newState[offerPageStateIndex] = state
    return selector(...newState)
  })
}

export function takeCurrentOfferState(state: OfferPageState) {
  return takeOfferState(state, state.paginator?.currentOfferId)
}

function offerPageStateReducer(middleware: Array<any> = EmptyArray, log: boolean = false): Reducer<OfferPageState, OfferPageDispatchAction> {
  return createReducer<OfferPageState, OfferPageDispatchAction>(INITIAL_OFFER_PAGE_STATE, {
    [OfferPageActions.toggleConnectedRooms]: (state) => ({
      isMultiRoomConnected: !state.isMultiRoomConnected,
    }),
    [OfferPageActions.checkAvailability]: (state, action) => ({
      flights: action.flights,
      room: action.room,
      activeFilter: action.filter,
      selectedPackageView: action.selectedPackageView,
      package: action.selectedPackageView?.package,
    }),
    [OfferPageActions.closeFilter]: (state, action) => ({
      activeFilter: undefined,
      ...(!action.occupancyPicker && { selectedPackageView: undefined }),
    }),
    [OfferPageActions.filterContinue]: () => ({
      activeFilter: undefined,
    }),
    [OfferPageActions.filterBack]: () => ({
      activeFilter: undefined,
    }),
    [OfferPageActions.clearFilter]: () => ({
      activeFilter: undefined,
    }),
    [OfferPageActions.closeSubFilter]: () => ({
      activeSubFilter: undefined,
    }),
    [OfferPageActions.closeHeaderFilter]: () => ({
      activeHeaderFilter: undefined,
    }),
    [OfferPageActions.resetDates]: (state, action) => {
      if (action.offerId) {
        return setBundledOfferData(action.offerId, 'checkOutDate', setBundledOfferData(action.offerId, 'checkInDate', state, undefined), undefined)
      }

      return {
        checkInDate: undefined,
        checkOutDate: undefined,
      }
    },
    [OfferPageActions.setDepartureMonth]: (state, action) => ({
      departureMonth: action.month,
    }),
    [OfferPageActions.setDepartureYear]: (state, action) => {
      if (
        // we're settting a year and we have a month already set
        state.departureMonth && action.year &&
        // the years don't match
        state.departureMonth.year !== action.year
      ) {
        // reset the selected 'month' if it's no longer the same year as the one the user selects
        return {
          departureYear: action.year,
          departureMonth: undefined,
        }
      }
      return {
        departureYear: action.year,
      }
    },
    [OfferPageActions.setTourV2Departure]: (state, action) => ({
      tourV2Departure: action.departure,
    }),
    [OfferPageActions.setTourV2Variation]: (state, action) => ({
      tourV2Variation: action.variation,
    }),
    [OfferPageActions.setAirport]: (state, action) => ({
      airport: action.airport,
    }),
    [OfferPageActions.setBedBankOffer]: (state, action) => ({
      bedbankOffer: action.bedbankOffer,
    }),
    [OfferPageActions.setPaginatorCurrentOfferId]: (state, action) => {
      const allOffersIds = Object.keys(state.bundledOffers ?? {})
      const paginator = {
        currentOfferId: action.currentOfferId,
        nextOfferId: allOffersIds[allOffersIds.indexOf(action.currentOfferId) + 1],
        backOfferId: allOffersIds[allOffersIds.indexOf(action.currentOfferId) - 1],
      }

      if (paginator.nextOfferId && state.bundledOffers?.[paginator.nextOfferId]?.checkInDate && state.bundledOffers?.[paginator.currentOfferId]?.checkInDate) {
        if (isBundleAndSaveV2Enabled()) {
          return {
            paginator,
          }
        }
        return {
          paginator,
          ...setBundledOfferData(paginator.nextOfferId, 'checkOutDate', setBundledOfferData(paginator.nextOfferId, 'checkInDate', state, undefined), undefined),
        }
      }

      return {
        paginator,
      }
    },
    [OfferPageActions.setDates]: (state, action) => {
      if (action.offerId) {
        return setBundledOfferData(action.offerId, 'checkInDate', state, action.checkIn)
      }

      let duration = state.duration
      if (action.checkIn && action.checkOut) {
        duration = moment(action.checkOut).diff(action.checkIn, 'days')
      }

      return {
        checkOutDate: action.checkOut,
        checkInDate: action.checkIn,
        durationSelected: true,
        duration,
      }
    },
    [OfferPageActions.setCheckInDate]: (state, action) => {
      let checkOutDate: string | undefined = state.checkOutDate
      if (state.duration) {
        // need to auto update check out to keep it in sync if we have a duration
        if (action.checkIn) {
          checkOutDate = moment(action.checkIn).add(state.duration, 'days').format(ISO_DATE_FORMAT)
        } else {
          checkOutDate = undefined
        }
      }
      if (action.offerId) {
        return setBundledOfferData(action.offerId, 'checkInDate', state, action.checkIn)
      }

      return {
        checkOutDate,
        checkInDate: action.checkIn,
        durationSelected: true,
      }
    },
    [OfferPageActions.setCheckOutDate]: (state, action) => {
      if (action.offerId) {
        return setBundledOfferData(action.offerId, 'checkOutDate', state, action.checkOut)
      }

      let duration = state.duration
      if (state.checkInDate && action.checkOut) {
        duration = moment(action.checkOut).diff(state.checkInDate, 'days')
      }

      return {
        checkOutDate: action.checkOut,
        duration,
        durationSelected: true,
      }
    },
    [OfferPageActions.setDuration]: (state, action) => {
      if (action.offerId) {
        return setBundledOfferData(action.offerId, 'duration', state, action.duration)
      }

      let checkOutDate: string | undefined = state.checkOutDate
      if (action.duration) {
        // need to auto update check out to keep it in sync
        if (state.checkInDate) {
          checkOutDate = moment(state.checkInDate).add(action.duration, 'days').format(ISO_DATE_FORMAT)
        }
      }

      return {
        checkOutDate,
        duration: action.duration,
        // duration changed, the selected package view is no longer possible, so clear it
        selectedPackageView: action.duration !== state.duration ? undefined : state.selectedPackageView,
        package: action.duration !== state.duration ? undefined : state.package,
        durationSelected: true,
      }
    },
    [OfferPageActions.setFilteredBundlePackageId]: (state, action) => ({
      filteredBundlePackageId: action.filteredBundlePackageId,
    }),
    [OfferPageActions.reverseBundles]: (state) => {
      const [offerStateOne, offerStateTwo] = Object.values(state.bundledOffers)
      if (!offerStateOne?.offer || !offerStateTwo?.offer) {
        return state
      }

      const newOrderedObject: Record<string, BundledOfferState> = {}
      newOrderedObject[offerStateTwo.offer.id] = offerStateTwo
      newOrderedObject[offerStateOne.offer.id] = offerStateOne
      return {
        bundledOffers: newOrderedObject,
      }
    },
    [OfferPageActions.setFlights]: (state, action) => ({
      flights: action.flights,
      multipleRoomPackages: setFlightsForAllRooms(state, !!action.flights),
    }),
    [OfferPageActions.setOffer]: (state, action) => {
      return {
        bundleOffer: isBundleOffer(action.offer) ? action.offer : undefined,
        offer: action.offer,
        bundledOffers: action.bundledOffers,
      }
    },
    [OfferPageActions.setRoomType]: (state, action) => {
      if (action.offerId) {
        return setBundledOfferData(action.offerId, 'room', state, action.room)
      }

      return {
        room: action.room,
        // room changed, selected view is no longer possible as it was for a previous room
        selectedPackageView: action.room !== state.room ? undefined : state.selectedPackageView,
        package: action.room !== state.room ? undefined : state.package,
      }
    },
    [OfferPageActions.setRooms]: (state, action) => ({
      rooms: action.rooms,
      multipleRoomPackages: action.rooms && action.rooms.length > 1 ? removeRoomlessSelectedPackages(action.rooms, state.multipleRoomPackages) : [],
    }),
    [OfferPageActions.setBedbankRoomId]: (state, action) => ({
      selectedBedbankRoomId: action.bedbankRoomId,
    }),
    [OfferPageActions.setTourV2Offer]: (state, action) => ({
      tourV2Variation: action.variation,
      tourV2Offer: action.offer,
    }),
    [OfferPageActions.setRoomOption]: (state, action) => ({
      roomOption: action.roomOption,
    }),
    [OfferPageActions.toggleFilter]: (state, action) => {
      if (action.offerId) {
        const filterShouldBeClosed = action.filter === state.bundledOffers[action.offerId].activeFilter
        if (filterShouldBeClosed) {
          return setBundledOfferData(action.offerId, 'activeFilter', state, undefined)
        }
        return setBundledOfferData(action.offerId, 'activeFilter', state, action.filter)
      }

      const filterShouldBeClosed = action.filter === state.activeFilter
      if (filterShouldBeClosed) {
        return {
          activeFilter: undefined,
        }
      }

      return {
        activeFilter: action.filter,
      }
    },
    [OfferPageActions.openSwapModal]: (state) => {
      return {
        bundleSwapModalTicker: state.bundleSwapModalTicker + 1,
      }
    },
    [OfferPageActions.toggleSubFilter]: (state, action) => {
      const filterShouldBeClosed = action.filter === state.activeSubFilter
      if (filterShouldBeClosed) {
        return {
          activeSubFilter: undefined,
        }
      }

      return {
        activeSubFilter: action.filter,
      }
    },
    [OfferPageActions.showRecommendationsView]: (state, action) => ({
      showRecommendationsView: true,
      recommendationsMonthIndex: action.monthIndex ?? state.recommendationsMonthIndex,
      recommendationsCheckInDay: action.checkInDay ?? state.recommendationsCheckInDay,
    }),
    [OfferPageActions.hideRecommendationsView]: () => ({
      showRecommendationsView: false,
      recommendationsMonthIndex: undefined,
      recommendationsCheckInDay: undefined,
    }),
    [OfferPageActions.selectRoomsPackages]: (state, action) => {
      const roomIds = new Set(action.roomsPackages.map(rpgk => rpgk.room.roomId))
      const newRoomPackagesList = state.multipleRoomPackages.filter(rpkg => !roomIds.has(rpkg.roomId))

      action.roomsPackages.forEach(rpkg => {
        const targetRoomPackage = state.multipleRoomPackages.find(mrkpg => mrkpg.roomId === rpkg.room.roomId)
        newRoomPackagesList.push(
          targetRoomPackage ? {
            ...targetRoomPackage,
            packageId: rpkg.packageOption.id,
            roomRateId: rpkg.packageOption.roomRate.id,
            roomTypeId: rpkg.packageOption.roomType.id,
          } : {
            roomId: rpkg.room.roomId,
            packageId: rpkg.packageOption.id,
            roomRateId: rpkg.packageOption.roomRate.id,
            isFlightsSelected: false,
            roomTypeId: rpkg.packageOption.roomType.id,
          },
        )
      })

      return ({
        multipleRoomPackages: newRoomPackagesList,
      })
    },
    [OfferPageActions.selectRoomPackage]: (state, action) => {
      const { packageOption, room } = action
      const targetRoomPackage = state.multipleRoomPackages.find(rpkg => rpkg.roomId === room.roomId)
      return ({
        multipleRoomPackages: [
          ...state.multipleRoomPackages.filter(rpkg => rpkg.roomId !== room.roomId),
          targetRoomPackage ? {
            ...targetRoomPackage,
            packageId: packageOption.id,
            roomRateId: packageOption.roomRate.id,
            roomTypeId: packageOption.roomType.id,
          } : {
            roomId: room.roomId,
            packageId: packageOption.id,
            roomRateId: packageOption.roomRate.id,
            isFlightsSelected: false,
            roomTypeId: packageOption.roomType.id,
          },
        ],
      })
    },
    [OfferPageActions.toggleFlightsPrice]: (state, action) => {
      const multipleRoomPackages = setFlightsForAllRooms(state, action.isFlightsSelected)
      return ({
        multipleRoomPackages,
        flights: action.isFlightsSelected,
      })
    },
    [OfferPageActions.setDisplayMemberPrice]: (state, action) => ({
      displayMemberPrice: action.displayMemberPrice,
    }),
    [OfferPageActions.setKeepBrowsing]: (state, action) => ({
      keepBrowsing: action.keepBrowsing,
    }),
    [OfferPageActions.setLastInteractedCalendarDay]: (state, action) => ({
      lastInteractedCalendarDay: action.lastInteractedCalendarDay,
    }),
    [OfferPageActions.toggleLoyaltyTargetRoomUpgrade]: (state, action) => {
      return {
        loyaltyTargetRoomUpgradesSelected: {
          ...state.loyaltyTargetRoomUpgradesSelected,
          [action.selectedUpgradeKey]: action.hasSelectedTargetRoomUpgrade,
        },
      }
    },
    [OfferPageActions.toggleHeaderFilter]: (_, action) => ({
      activeHeaderFilter: action.filter,
    }),
    [OfferPageActions.toggleMobileMultiRoomMode]: (_, action) => ({
      mobileMultiRoomMode: action.mobileMultiRoomMode,
    }),
    [OfferPageActions.toggleMultiRoomSelectRooms]: (_, action) => ({
      isMultiRoomSelectRooms: action.isMultiRoomSelectRooms,
    }),
  },
  middleware,
  log && config.LOG_REACT_REDUCERS,
  )
}

export default offerPageStateReducer
